import { Fragment, useEffect, useMemo, useRef } from "react";
import { defaultLookup, defaultLookupId } from "../utils";
import { styles } from "./styles";
import ChatMessage from "./Chat.Message";
import { ChatMessagesProps } from "./types";
import Loading from "../Loading";
import ChatCues from "./Chat.Cues";
import { useResizeObserver } from "../../hooks";

/**
 * `Chat.Messages` is a component that renders a list of chat messages.
 * Commonly used within the `Chat` component, it provides a scrollable area for messages and handles the display of loading states and empty states.
 * ```tsx
 * <Chat>
 *   <Chat.Messages messages={messages} .... />
 *   ...
 * </Chat>
 * ```
 */
function ChatMessages<T>({
  messages,
  isWaitingForResponse,
  emptyState,
  children,
  lookupIcon,
  lookupContent = defaultLookupContent,
  lookupTypeOfContent,
  lookupReceived,
  lookupId = defaultLookupId,
  lookupSortValue,
  lookupBottomActions,
  renderMessage,
  renderAttachments,
  attachmentsPosition = "top",
  cues
}: ChatMessagesProps<T>) {
  const messagesRef = useRef<HTMLDivElement>(null);
  const userHasScrolled = useRef(false);

  const sortedMessages = useMemo(() => {
    if (!lookupSortValue) {
      return messages;
    }
    return [...messages].sort((a, b) => {
      const aTime = lookupSortValue?.(a) ?? 0;
      const bTime = lookupSortValue?.(b) ?? 0;

      return aTime - bTime;
    });
  }, [messages, lookupSortValue]);

  function scrollToBottom() {
    if (
      !messagesRef.current ||
      typeof messagesRef.current.scrollTo !== "function" ||
      userHasScrolled.current
    ) {
      return;
    }

    messagesRef.current?.scrollTo?.({
      top: messagesRef.current?.scrollHeight,
      behavior: "smooth"
    });
  }

  function onScroll() {
    if (!messagesRef.current) {
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = messagesRef.current;
    userHasScrolled.current = scrollTop + clientHeight < scrollHeight - 20;
  }

  useEffect(() => {
    userHasScrolled.current = false;
  }, [isWaitingForResponse]);

  // Scroll to bottom when new messages arrive
  useEffect(() => scrollToBottom(), [sortedMessages]);

  useResizeObserver(messagesRef, () => {
    // Scroll to bottom when the messages container resizes (e.g. due to images loading)
    scrollToBottom();
  });

  return (
    <div
      className="kit-ChatMessages"
      css={[styles.messages.base]}
      ref={messagesRef}
      onScroll={onScroll}
    >
      {!sortedMessages.length && emptyState}

      {sortedMessages.map((message, index) => {
        const content = lookupContent(message);
        const id = lookupId(message);
        return (
          <Fragment key={id}>
            {typeof renderMessage === "function" ? (
              renderMessage(message, index)
            ) : (
              <ChatMessage
                type={lookupTypeOfContent?.(message)}
                content={typeof content === "string" ? content : undefined}
                received={lookupReceived?.(message)}
                icon={lookupIcon?.(message)}
                bottomActions={lookupBottomActions?.(message)}
                attachments={renderAttachments?.(message)}
                attachmentsPosition={attachmentsPosition}
              >
                {typeof content !== "string" ? content : undefined}
              </ChatMessage>
            )}
          </Fragment>
        );
      })}
      {isWaitingForResponse && (
        <ChatMessage key="loading" received>
          <Loading.Bubbles />
        </ChatMessage>
      )}

      {!isWaitingForResponse && cues && (
        <ChatCues
          {...cues}
          onExpanded={() => {
            cues.onExpanded?.();
            scrollToBottom();
          }}
          onRevealed={() => {
            cues.onRevealed?.();
            scrollToBottom();
          }}
        />
      )}

      {children}
    </div>
  );
}

function defaultLookupContent<T>(message: T): string | undefined | null {
  return defaultLookup(message, {
    keys: ["content", "message", "text", "body"]
  });
}

export default ChatMessages;
